import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FLAG_LABELS, unknownCodes, setTripCodes, loadTripCodes } from "@/lib/tripCodes";
import { Loader2, Plus } from "lucide-react";

export function UnknownCodesBadge({ trips, canEdit, navigate }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [, setTick] = useState(0);
  const list = unknownCodes(trips);
  if (!list.length) return null;
  const onSaved = () => { setEditing(null); setTick((n) => n + 1); };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="unknown-codes-badge"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-[11px] text-slate-400 transition-colors hover:text-slate-200">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> {list.length} unknown trip code{list.length === 1 ? "" : "s"}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-80 rounded-lg border border-slate-700 bg-[#0f172a] p-3 text-xs shadow-xl" data-testid="unknown-codes-list">
          <p className="mb-2 text-slate-400">These codes are in trips on this list but aren't in your trip codes yet, so dispatch treats them as normal trips.</p>
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {list.map(([c, n]) => (
              <li key={c} data-testid={`unknown-code-${c}`}>
                <div className="flex items-center justify-between gap-2 font-mono text-slate-200">
                  <span>{c}</span>
                  <span className="flex items-center gap-2 text-slate-500">{n} trip{n === 1 ? "" : "s"}
                    {canEdit && editing !== c && (
                      <button onClick={() => setEditing(c)} className="inline-flex items-center gap-0.5 font-sans font-semibold text-blue-300 hover:underline" data-testid={`unknown-code-define-${c}`}><Plus className="h-3 w-3" /> Define</button>
                    )}
                  </span>
                </div>
                {editing === c && <DefineForm code={c} onCancel={() => setEditing(null)} onSaved={onSaved} />}
              </li>
            ))}
          </ul>
          {canEdit && <button onClick={() => navigate("/portal/settings#trip-codes")} className="mt-2 text-slate-400 hover:text-slate-200 hover:underline" data-testid="unknown-codes-define">Manage all codes in Settings →</button>}
        </div>
      )}
    </div>
  );
}

function DefineForm({ code, onCancel, onSaved }) {
  const [f, setF] = useState({ meaning: "", aba: false, wheelchair: false, alone: false, shared: false, monitor: false, escort: false });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data: cur } = await api.get("/trip-codes");
      const { data } = await api.put("/trip-codes", { codes: [...(cur.codes || []), { code, ...f, meaning: f.meaning.trim() }] });
      setTripCodes(data.codes); loadTripCodes(true);
      toast.success(`${code} added to your trip codes.`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-1.5 space-y-2 rounded-md border border-slate-700 bg-slate-900/80 p-2" data-testid={`define-form-${code}`}>
      <input autoFocus value={f.meaning} onChange={(e) => setF({ ...f, meaning: e.target.value })} placeholder="What does it mean? e.g. Cane user"
        className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-600" data-testid={`define-meaning-${code}`} />
      <div className="grid grid-cols-2 gap-1">
        {Object.entries(FLAG_LABELS).map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5 text-slate-300">
            <input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} className="h-3.5 w-3.5 accent-blue-600" data-testid={`define-${k}-${code}`} /> {l}
          </label>
        ))}
      </div>
      <p className="text-[10px] text-slate-500">Leave all unticked for a note-only code (no change to dispatch).</p>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-200" data-testid={`define-cancel-${code}`}>Cancel</button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 rounded bg-blue-700 px-2.5 py-1 font-semibold text-white hover:bg-blue-800 disabled:opacity-50" data-testid={`define-save-${code}`}>
          {saving && <Loader2 className="h-3 w-3 animate-spin" />} Save code
        </button>
      </div>
    </div>
  );
}
